// ── COACHING: day-of print sheets ────────────────────────────────────
// Two sheets from one button. Lori gets a run sheet — the whole day, every
// coach as a column, with the parent phone under each name, because she is the
// one who fields "we're stuck in traffic". Each coach then gets their own page
// with just their column and no contact details: a coach chases nobody, and
// spreading a dozen family phone numbers across every printout in the building
// has no upside.
//
// Printed black on white rather than the portal's dark theme — these get
// carried around a studio and read at arm's length, and a dark page is both
// unreadable on paper and a waste of toner. Same window.open + document.write
// shape as the Enrollment contact list.
import { apiFetch } from '../core/api.js';
import { toast, toastError } from '../core/ui.js';

const fmtTime = hhmm => {
  const [h, m] = hhmm.split(':').map(Number);
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'pm' : 'am'}`;
};

const fmtDay = iso => new Date(iso + 'T00:00')
  .toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

// A row a coach can act on: someone booked, a break, or genuinely nothing.
// Empty slots are printed rather than dropped so a gap reads as a real gap and
// not as a page that failed to finish printing.
function coachRow(r) {
  if (r.performerName) {
    return `<div class="row">
      <span class="time">${fmtTime(r.startsAt)}</span>
      <span class="who">${esc(r.performerName)}</span>
      <span class="meta">${r.nth > 1 ? 'second session' : ''}</span>
    </div>`;
  }
  if (r.slotStatus === 'break') {
    return `<div class="row"><span class="time">${fmtTime(r.startsAt)}</span><span class="who brk">Break</span><span class="meta"></span></div>`;
  }
  if (r.slotStatus === 'off') return '';
  return `<div class="row"><span class="time">${fmtTime(r.startsAt)}</span><span class="who open">No one booked</span><span class="meta"></span></div>`;
}

function masterSheet(event, coaches) {
  // Every coach shares the event's slot times, so the first column supplies the
  // rows and the rest line up against it.
  const times = (coaches[0]?.rows || []).map(r => r.startsAt);
  const cell = (coach, time) => {
    const r = (coach.rows || []).find(x => x.startsAt === time);
    if (!r) return '<td class="off">—</td>';
    if (r.performerName) {
      return `<td>${esc(r.performerName)}${r.phone ? `<span class="sub">${esc(r.phone)}</span>` : '<span class="sub none">no phone on file</span>'}</td>`;
    }
    if (r.slotStatus === 'break') return '<td class="brk">Break</td>';
    if (r.slotStatus === 'off') return '<td class="off">—</td>';
    return '<td class="off">open</td>';
  };

  const booked = coaches.reduce((n, c) => n + c.rows.filter(r => r.performerName).length, 0);
  const performers = new Set();
  coaches.forEach(c => c.rows.forEach(r => r.performerName && performers.add(r.performerName)));

  return `<div class="sheet">
    <div class="hd">
      <div>
        <div class="kicker">Center Stage Youth Performers</div>
        <h1>${esc(event.name)} — run sheet</h1>
        <div class="sub-line">${fmtDay(event.eventDate)} · ${fmtTime(event.startsAt)} – ${fmtTime(event.endsAt)}</div>
      </div>
      <div class="tally"><div><b>${booked}</b> sessions</div><div><b>${performers.size}</b> performers</div></div>
    </div>
    <table>
      <thead><tr><th style="width:76px">Time</th>${coaches.map(c => `<th>${esc(c.name)}</th>`).join('')}</tr></thead>
      <tbody>${times.map(t => `<tr><td class="t">${fmtTime(t)}</td>${coaches.map(c => cell(c, t)).join('')}</tr>`).join('')}</tbody>
    </table>
    <div class="foot">Phone numbers are the parent on the account. Each coach's own sheet follows, without contact details.</div>
  </div>`;
}

function coachSheet(event, coach) {
  const rows = (coach.rows || []).map(coachRow).join('');
  const count = coach.rows.filter(r => r.performerName).length;
  return `<div class="sheet break-before">
    <div class="hd">
      <div>
        <div class="kicker">Center Stage Youth Performers</div>
        <h1>${esc(coach.name)}</h1>
      </div>
      <div class="tally right">
        <div class="ser">${esc(event.name)}</div>
        <div>${fmtDay(event.eventDate)}</div>
        <div>${fmtTime(event.startsAt)} – ${fmtTime(event.endsAt)} · ${event.slotMinutes}-minute sessions</div>
      </div>
    </div>
    <hr class="rule">
    ${rows}
    <div class="foot solid"><b>${count} session${count === 1 ? '' : 's'}</b> today</div>
  </div>`;
}

export async function printDaySheets(eventId) {
  let data;
  try {
    data = await apiFetch('/coaching/events/' + eventId + '/daysheets');
  } catch (e) {
    toastError(e.message);
    return;
  }

  const coaches = (data.coaches || []).filter(c => (c.rows || []).length);
  if (!coaches.length) {
    toast('Add coaches to this day before printing');
    return;
  }

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
  <meta http-equiv="Content-Security-Policy" content="default-src 'self'; style-src 'self' 'unsafe-inline' https://fonts.googleapis.com; font-src https://fonts.gstatic.com;">
  <title>Day sheets — ${esc(data.event.name)}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:wght@400;700&family=Source+Sans+3:wght@300;400;500;600&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: 'Source Sans 3', sans-serif; font-weight: 300; color: #1a1a2e; background: #fff; }
    .sheet { padding: 44px 48px; max-width: 820px; margin: 0 auto; }
    .break-before { page-break-before: always; }
    .hd { display: flex; align-items: flex-end; justify-content: space-between; gap: 24px; margin-bottom: 16px; }
    .kicker { font-size: 10px; font-weight: 600; letter-spacing: 0.14em; text-transform: uppercase; color: #9e9488; margin-bottom: 6px; }
    h1 { font-family: 'Libre Baskerville', serif; font-size: 26px; font-weight: 700; line-height: 1.15; }
    .sub-line { font-size: 13px; color: #6b6459; margin-top: 4px; }
    .tally { font-size: 12px; color: #6b6459; text-align: right; line-height: 1.5; }
    .tally b { color: #1a1a2e; }
    .tally .ser { font-family: 'Libre Baskerville', serif; font-size: 15px; color: #1a1a2e; }
    .rule { border: 0; border-top: 2px solid #1a1a2e; margin: 0 0 4px; }
    table { width: 100%; border-collapse: collapse; }
    th { text-align: left; font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase; color: #9e9488; padding: 0 8px 8px; border-bottom: 2px solid #1a1a2e; }
    td { padding: 9px 8px; border-bottom: 1px solid #e8e4dc; font-size: 13px; vertical-align: top; }
    td.t { font-family: 'Libre Baskerville', serif; font-weight: 700; font-variant-numeric: tabular-nums; white-space: nowrap; }
    td .sub { display: block; font-size: 10px; color: #6b6459; font-variant-numeric: tabular-nums; }
    td .sub.none { font-style: italic; }
    td.off { color: #b4aea4; }
    td.brk { color: #6b6459; font-style: italic; }
    .row { display: flex; align-items: baseline; gap: 20px; padding: 12px 0; border-bottom: 1px solid #e8e4dc; }
    .row .time { font-family: 'Libre Baskerville', serif; font-size: 17px; font-weight: 700; width: 110px; flex-shrink: 0; font-variant-numeric: tabular-nums; }
    .row .who { font-size: 16px; flex-grow: 1; }
    .row .who.brk { color: #6b6459; font-style: italic; }
    .row .who.open { color: #b4aea4; }
    .row .meta { font-size: 11px; color: #6b6459; }
    .foot { margin-top: 20px; padding-top: 12px; font-size: 11px; color: #6b6459; line-height: 1.6; }
    .foot.solid { border-top: 2px solid #1a1a2e; font-size: 13px; color: #1a1a2e; }
    tr, .row { page-break-inside: avoid; }
    thead { display: table-header-group; }
    @page { margin: 12mm; }
  </style></head><body>
  ${masterSheet(data.event, coaches)}
  ${coaches.map(c => coachSheet(data.event, c)).join('')}
<script><\/script>
</body></html>`;

  const w = window.open('', '_blank');
  if (!w) { toast('Allow pop-ups to print the day sheets'); return; }
  w.document.write(html);
  w.document.close();
}
